export const getProjectOptions = (projects = {}) =>
  Object.entries(projects || {}).map(([value, label]) => ({ value, label }));

export const getContactProjects = (contact) =>
  (Array.isArray(contact.userProjects) ? contact.userProjects : []).map(
    ({ projectID, projectName }) => ({
      value: String(projectID),
      label: projectName || String(projectID),
    }),
  );

export const getContactName = (contact) =>
  [contact["First Name"], contact["Last Name"]].filter(Boolean).join(" ") ||
  contact["Email Address"] ||
  "Unnamed contact";

export const getContactKey = (contact, company) =>
  JSON.stringify([company, contact["Email Address"]]);

export const getContactValues = (contact) => ({
  firstName: contact["First Name"] || "",
  lastName: contact["Last Name"] || "",
  inviteeEmail: contact["Email Address"] || "",
  inviteePhoneNumber: String(contact.Phone || ""),
  inviteeCompany: contact.Company || "",
  inviteeCompanyAddress: contact["Company Address"] || "",
  inviteeCompanyPermission: contact["Company Permissions"],
  inviteeProjectPermission: contact["Project Permissions"],
});

export const getContactFormValues = (contact) => ({
  ...getContactValues(contact),
  projectIds: getContactProjects(contact).map(({ value }) => value),
  projectEditScope: "all",
  inviteeProjectsToEdit: [],
  inviteeCompanyPermission: contact["Company Permissions"] || "Default",
  inviteeProjectPermission: contact["Project Permissions"] || "Default",
});

export const getEditDetails = (values, contact) => ({
  firstName: values.firstName.trim(),
  lastName: values.lastName.trim(),
  inviteeEmail: contact["Email Address"],
  inviteePhoneNumber: String(values.inviteePhoneNumber || "").trim(),
  inviteeCompany: values.inviteeCompany.trim(),
  inviteeCompanyAddress: (values.inviteeCompanyAddress || "").trim(),
  inviteeCompanyPermission: contact["Company Permissions"],
  inviteeProjectPermission: contact["Project Permissions"],
});

export const getContactAssignmentDetails = (values, contact) => ({
  ...getEditDetails(values, contact),
  inviteeCompanyPermission: values.inviteeCompanyPermission,
  inviteeProjectPermission: values.projectIds?.length ? values.inviteeProjectPermission || "Default" : "Default",
  projectsToAddUserTo: values.projectIds || [],
});

export const getContactUpdateDetails = (values, contact) => {
  const { projectsToAddUserTo, ...details } = getContactAssignmentDetails(values, contact);
  const selectedProjects = new Set(projectsToAddUserTo.map(String));
  const existingProjects = new Set(getContactProjects(contact).map(({ value }) => value));
  return {
    ...details,
    inviteeProjectsToAdd: [...selectedProjects].filter((id) => !existingProjects.has(id)),
    inviteeProjectsToEdit:
      values.projectEditScope === "selected"
        ? [...new Set((values.inviteeProjectsToEdit || []).map(String))]
        : [...selectedProjects],
    inviteeProjectsToRemove: [...existingProjects].filter((id) => !selectedProjects.has(id)),
  };
};

export const getBulkEditDetails = (values, contacts) =>
  contacts.map((contact) => ({
    ...getEditDetails(
      {
        ...getContactValues(contact),
        inviteeCompany: values.inviteeCompany?.trim() || contact.Company || "",
        inviteeCompanyAddress:
          values.inviteeCompanyAddress?.trim() ||
          contact["Company Address"] ||
          "",
      },
      contact,
    ),
    inviteeProjectsToAdd: [],
    inviteeProjectsToRemove: [],
  }));

export const getInvitationDetails = (contact) => ({
  ...getContactValues(contact),
  inviteeCompanyPermission: contact["Company Permissions"],
  inviteeProjectPermission: contact["Project Permissions"],
});

export const canManageContact = (accessLevel, contact) =>
  [2, 3].includes(Number(accessLevel)) &&
  contact["Company Permissions"] !== "Account Owner" &&
  contact["Project Permissions"] !== "Account Owner";

export const filterCompanies = (content = {}, search = "") => {
  const query = search.trim().toLowerCase();
  return Object.entries(content || {})
    .map(([company, contacts]) => ({
      company,
      contacts: (Array.isArray(contacts) ? contacts : []).filter((contact) =>
        [
          company,
          getContactName(contact),
          contact["Email Address"],
          contact.Phone,
          contact["Company Address"],
          contact.Status,
          ...getContactProjects(contact).map((project) => project.label),
        ].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(query),
        ),
      ),
    }))
    .filter(({ contacts }) => contacts.length > 0)
    .sort((a, b) =>
      a.company.localeCompare(b.company, undefined, { sensitivity: "base" }),
    );
};

export const getErrorMessage = (_, fallback) => fallback;
